import { createContext } from "react";
import { Tenant, TenantSettings } from "@/hooks/useTenants";
import { Service } from "@/hooks/useServices";
import { Category } from "@/hooks/useCategories";
import { TeamMember } from "@/hooks/useTeamMembers";
import { Resource } from "@/hooks/useResources";
import { Customer } from "@/hooks/useCustomers";
// Import Appointment from the main useAppointments hook file
import { Appointment } from "@/hooks/useAppointmentsList";

// Re-export for convenience
export type { Appointment };

export interface TenantContextType {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  services: Service[];
  allServices: Service[]; // Includes deleted services
  categories: Category[];
  teamMembers: TeamMember[];
  resources: Resource[];
  isInitializing: boolean;
  isLoadingNonCritical: boolean;
  /**
   * Gesetzt, wenn die Salon-Liste nicht geladen werden konnte. Ohne das laesst
   * sich "Stoerung" nicht von "kein Salon zugeordnet" unterscheiden -- und die
   * Oberflaeche zeigte bei einer Stoerung faelschlich Letzteres.
   */
  tenantsLoadError: Error | null;
  retryTenants: () => void;
  setCurrentTenant: (tenant: Tenant) => void;
  // Returns the created row: callers need its id to link team members right after creation.
  addService: (service: Omit<Service, 'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'appointment_type_categories'>) => Promise<Service | null>;
  updateService: (id: string, service: Partial<Omit<Service, 'appointment_type_categories'>>) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  addTeamMember: (teamMember: Omit<TeamMember, 'id' | 'created_at' | 'updated_at'>) => Promise<{ id: string }>;
  updateTeamMember: (id: string, teamMember: Partial<TeamMember>) => Promise<void>;
  deleteTeamMember: (id: string) => Promise<void>;
  pauseTeamMember: (id: string, until?: string | null, reason?: string | null) => Promise<void>;
  resumeTeamMember: (id: string) => Promise<void>;
  addResource: (resource: Omit<Resource, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateResource: (id: string, resource: Partial<Resource>) => Promise<void>;
  deleteResource: (id: string) => Promise<void>;
  addCustomer: (customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>) => Promise<{ data: Customer | null; error: Error | null }>;
  updateCustomer: (id: string, customer: Partial<Customer>) => Promise<void>;
  updateTenantSettings: (settings: TenantSettings) => Promise<void>;
  updateTenantLanguage: (language: string) => Promise<void>;
  updateTenantName: (name: string) => Promise<void>;
  updateTenantLogo: (logoUrl: string) => Promise<void>;
  updateTenantPrimaryColor: (primaryColor: string) => Promise<void>;
  updateTenantAddress: (address: string, city?: string) => Promise<void>;
  updateTenantEmail: (email: string) => Promise<void>;
  updateTenantGoogleProfile: (googleData: {
    google_place_id: string;
    google_cid?: string;
    google_maps_review_url?: string;
    location_coordinate?: string;
  }) => Promise<void>;
  updateTenantGBPConnection: (gbpData: {
    gbp_access_token: string | null;
    gbp_refresh_token: string | null;
    gbp_token_expires_at: string | null;
    gbp_account_id: string | null;
    gbp_location_id: string | null;
    gbp_location_name: string | null;
    gbp_connected_at: string | null;
  }) => Promise<void>;
  updateBookingNotice: (enabled: boolean, text: string | null) => Promise<void>;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);